import React, { useState, useEffect } from "react";
import { Alert, Button, Card, Form, Input } from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import '../assets/css/Dentobook.css';
import { CloseOutlined } from "@ant-design/icons";
import { isMobileOnly } from "react-device-detect";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { getAuthenticationApi } from "../api/apiInstances";

const ResetPassword = () => {
    const intl = useIntl();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [errMsg, setErrMsg] = useState<string>('');
    const [successMsg, setSuccessMsg] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [token, setToken] = useState<string>('');

    const [form] = Form.useForm();

    useEffect(() => {
        // Get token from URL params
        const tokenParam = searchParams.get('token');
        if (!tokenParam) {
            setErrMsg(intl.formatMessage({ id: 'invalid_reset_token' }));
            return;
        }
        setToken(tokenParam);
    }, [searchParams, intl]);

    const validatePassword = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(intl.formatMessage({ id: 'new_password_required' }));
        }
        if (value.length < 9) {
            return Promise.reject(intl.formatMessage({ id: 'password_min_length_error' }));
        }
        if (!/[0-9]/.test(value)) {
            return Promise.reject(intl.formatMessage({ id: 'password_must_contain_number' }));
        }
        if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'/`~]/.test(value)) {
            return Promise.reject(intl.formatMessage({ id: 'password_must_contain_special' }));
        }
        if (!/[A-Z]/.test(value)) {
            return Promise.reject(intl.formatMessage({ id: 'password_must_contain_uppercase' }));
        }
        if (!/[a-z]/.test(value)) {
            return Promise.reject(intl.formatMessage({ id: 'password_must_contain_lowercase' }));
        }
        return Promise.resolve();
    };

    const validateConfirmPassword = (_: any, value: string) => {
        if (!value) {
            return Promise.reject(intl.formatMessage({ id: 'confirm_password_required' }));
        }
        if (value !== newPassword) {
            return Promise.reject(intl.formatMessage({ id: 'passwords_do_not_match' }));
        }
        return Promise.resolve();
    };

    const onFinish = async () => {
        try {
            await form.validateFields();
            setLoading(true);
            setErrMsg('');
            setSuccessMsg('');

            if (!token) {
                setErrMsg(intl.formatMessage({ id: 'invalid_reset_token' }));
                setLoading(false);
                return;
            }

            // Call reset password endpoint using generated API client
            const authApi = getAuthenticationApi();
            const response = await authApi.confirmPasswordReset({
                token: token,
                new_password: newPassword
            });

            setSuccessMsg(response.data.message || intl.formatMessage({ id: 'password_reset_success' }));

            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate('/login');
            }, 2000);

        } catch (err: any) {
            if (err.response?.data?.detail) {
                if (Array.isArray(err.response.data.detail)) {
                    // Pydantic validation errors
                    const errors = err.response.data.detail.map((e: any) => e.msg).join(', ');
                    setErrMsg(errors);
                } else {
                    setErrMsg(err.response.data.detail);
                }
            } else {
                setErrMsg(intl.formatMessage({ id: 'unexpected_error' }));
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh",
                overflow: "hidden",
            }}
        >
            <div style={{ marginBottom: "20px", textAlign: "center" }}>
                <a href="https://dentobook.com/en/" target="_blank" rel="noopener noreferrer">
                    <img
                        alt="logo"
                        className="dentobook-logo"
                        src={require("../assets/logo.png")}
                        style={{
                            width: "420px",
                            height: "104px",
                            padding: "13px 10px 13.65px 10px",
                        }}
                    />
                </a>
            </div>
            <Card
                style={{
                    width: isMobileOnly ? "90vw" : 480,
                    padding: "40px",
                    borderRadius: "30px",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)"
                }}
            >
                <div style={{ marginBottom: "24px", textAlign: "center" }}>
                    <h2 style={{ marginBottom: "8px", fontSize: "24px", fontWeight: 600 }}>
                        <FormattedMessage id="reset_password_title" />
                    </h2>
                    <p style={{ color: "#666", fontSize: "14px", margin: 0 }}>
                        <FormattedMessage id="reset_password_description" />
                    </p>
                </div>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    style={{ textAlign: "left" }}
                >
                    {successMsg ? (
                        <Alert
                            message={successMsg}
                            description={<FormattedMessage id="redirecting_to_login" />}
                            type="success"
                            className="mb-24"
                            showIcon
                        />
                    ) : null}

                    {errMsg ? (
                        <Alert
                            message={errMsg}
                            type="error"
                            className="mb-24"
                            closeIcon={<CloseOutlined />}
                            onClose={() => setErrMsg('')}
                            showIcon
                            onClick={() => setErrMsg('')}
                        />
                    ) : null}

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="new_password" />}
                        name="newPassword"
                        rules={[{ validator: validatePassword }]}
                        extra={
                            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                                <FormattedMessage id="password_requirements_hint" />
                            </div>
                        }
                    >
                        <Input
                            type="password"
                            style={{ borderRadius: "12px", height: "48px" }}
                            autoComplete="new-password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            placeholder={intl.formatMessage({ id: 'new_password_placeholder' })}
                        />
                    </Form.Item>

                    <Form.Item
                        className={"dentobook-form-item dentobook-simple-input"}
                        label={<FormattedMessage id="confirm_new_password" />}
                        name="confirmPassword"
                        dependencies={['newPassword']}
                        rules={[{ validator: validateConfirmPassword }]}
                    >
                        <Input
                            type="password"
                            style={{ borderRadius: "12px", height: "48px" }}
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            placeholder={intl.formatMessage({ id: 'confirm_password_placeholder' })}
                        />
                    </Form.Item>

                    <div className="dentobook-register-btn">
                        <Form.Item className={"dentobook-form-item dentobook-simple-input"}>
                            <Button
                                className="dentobook-btn-primary dentobook-btn-medium"
                                type="primary"
                                htmlType="submit"
                                disabled={loading || !token || !!successMsg}
                                style={{
                                    width: "100%",
                                    height: "48px",
                                }}
                            >
                                {loading ? <FormattedMessage id="resetting" /> : <FormattedMessage id="reset_password_button" />}
                            </Button>
                        </Form.Item>
                    </div>

                    <div style={{ textAlign: "center", marginTop: "16px" }}>
                        <Link to="/login" style={{ color: "#1890ff", fontWeight: 500 }}>
                            <FormattedMessage id="back_to_login" />
                        </Link>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default ResetPassword;
